import {useState} from 'react';
import Button from '../../../../../../components/button';
import roundLastThreeToZero from '../../../../../../utilities/helper/unitConvertor/roundLastThreeToZero';

export interface ISelectedAds {
  id: string;
  title: string;
  ad_price: number; // discounted price
  original_price: number; // original back end price
  discount_amount: number; // original discount percentage
  editedPrice?: number; // optional manual override
  image: string;
}

interface IProps {
  ad: ISelectedAds;
  newPrice: number;
  onPriceChange: (price: number) => void;
  setIsEditingMode: (value: boolean) => void; // set state to disable the button
  increasePercentage: number;
}

const AdvertisementBulkEditableCard = ({
  ad,
  newPrice,
  onPriceChange,
  setIsEditingMode,
  increasePercentage,
}: IProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftPrice, setDraftPrice] = useState<string>('');

  const confirmEdit = () => {
    if (!draftPrice) return;
    onPriceChange(roundLastThreeToZero(Number(draftPrice)));
    setIsEditing(false);
    setIsEditingMode(false);
    setDraftPrice('');
  };

  const handleBlur = () => {
    if (!draftPrice) return;
    const num = Number(draftPrice);
    if (!isNaN(num)) {
      const rounded = roundLastThreeToZero(num);
      setDraftPrice(rounded.toString());
    }
  };

  const increasedOriginalPrice = roundLastThreeToZero(
    ad?.original_price * (1 + increasePercentage / 100),
  );

  return (
    <div
      className="border-[1px] shadow-sm rounded-lg p-[1rem] gap-[1rem] flex items-start justify-start text-md
        cursor-pointer transition-all duration-200"
    >
      <img src={ad.image} className="object-cover rounded-lg size-11" />
      <div className="flex flex-col space-y-2 w-full">
        <span className="font-medium">{ad.title}</span>

        {Boolean(ad.discount_amount) && (
          <div className="flex relative gap-2 items-center opacity-50 w-fit">
            <span className="font-normal text-gray-500 text-md">قیمت</span>
            <span className="absolute left-0 top-[10px] w-full -rotate-[3deg] opacity-100 bg-red-600 size-[2px]"></span>
            <span className="font-medium text-zinc-500">
              {increasedOriginalPrice?.toLocaleString()} تومان
            </span>
          </div>
        )}

        {!isEditing ? (
          <div className="flex justify-between items-center w-full">
            <div className="flex gap-2 items-center">
              <span className="font-normal text-gray-500 text-md">قیمت</span>
              <span className="font-medium text-zinc-500 text-md">
                {newPrice.toLocaleString()} تومان
              </span>
            </div>
            <i
              className="text-xl text-farm-shop-500 icon-nameEdit"
              onClick={() => {
                setDraftPrice('');
                setIsEditing(true);
                setIsEditingMode(true);
              }}
            />
          </div>
        ) : (
          <div className="flex gap-2 items-end">
            <div className="relative w-full">
              {/* Your label or other parts go here */}
              <label
                htmlFor="newPrice"
                className="font-normal text-gray-500 text-md"
              >
                قیمت
              </label>

              {/* Mirror div for inline tag */}
              <div className="flex absolute inset-0 top-5 items-center px-3 text-sm text-gray-900 pointer-events-none">
                <span>{draftPrice && Number(draftPrice).toLocaleString()}</span>
                {draftPrice && <span className="mr-1">تومان</span>}
              </div>

              {/* Your original input stays intact */}

              <input
                id="newPrice"
                name="newPrice"
                type="text"
                style={
                  draftPrice.length === 0
                    ? {color: 'black'}
                    : {color: 'transparent'}
                }
                inputMode="numeric"
                className="w-full px-3 py-2 border-[1px] border-gray-400 rounded-lg text-sm font-normal placeholder:text-sm focus:outline-none h-10 bg-transparent"
                placeholder="وارد کردن قیمت (تومان)"
                value={draftPrice}
                onChange={e => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) setDraftPrice(val.slice(0, 12)); // max 12 digits
                }}
                onBlur={handleBlur}
              />

              {/* Optional close button */}
              {draftPrice && (
                <i
                  onClick={() => {
                    setDraftPrice('');
                    setIsEditing(false);
                    setIsEditingMode(false);
                  }}
                  className="absolute text-gray-400 -translate-y-1/2 left-2 top-[60%] icon-nameClose-remove text-lg"
                />
              )}
            </div>
            {draftPrice && (
              <Button
                variant="outline"
                size="small"
                baseStyles="flex flex-1 items-center justify-center rounded-lg font-medium transition-colors outline-none text-md min-h-10"
                className="text-sm text-green-600"
                onClick={confirmEdit}
              >
                تایید
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvertisementBulkEditableCard;
